/**
 * The CRM adapter: where the signed-in member's meetings come from (their own CRM calendar, read by the
 * backend with their CRM token — `GET /v1/me/meetings`), and how a CRM event becomes a joinable link. The home screen's agenda and the "choose from
 * calendar" picker talk to this interface only; the CRM behind it is whichever
 * one config.crmApi points at (see docs/CRM_INTEGRATION.md for the REST
 * contract). Without a CRM API, or without a signed-in host, it is inert and
 * the UI says so.
 *
 * Interface (what src/ui/agenda.js and src/ui/calendar-picker.js use):
 *   supported()            a CRM is configured and someone is signed in
 *   available()            supported, and not backing off after a failure
 *   retryIn()              ms until the next attempt is worth making
 *   clearBackoff()         the user pressed Refresh
 *   onChange(fn)           subscribe to "the meetings changed"; returns unsubscribe
 *   listMeetings({from,to})  → { events, total }, `meeting.url` set on each event
 *   resolveLink(event)     the join URL for an event (from meeting.url or meeting.code)
 *   dispose()
 */
import { isSignedIn } from './auth.js';

export const LIST_LIMIT = 500;
const REQUEST_TIMEOUT_MS = 8000;
const RETRY_AFTER_MS = 60_000;
const MAX_RETRY_MS = 10 * 60_000;

const runtime = () => (typeof window !== 'undefined' && window.TRTMEET_CONFIG) || {};

/** The CRM answered and said no (4xx/5xx with a body); as opposed to being unreachable. */
export class CrmApiError extends Error {
  constructor(status, message) {
    super(message || `The CRM answered ${status}`);
    this.name = 'CrmApiError';
    this.status = status;
  }
}

const toMs = (value) => (typeof value === 'number' ? value : Date.parse(value));

/**
 * One CRM event in the shape the agenda reads: epoch-millisecond `start`/`end`,
 * and `meeting.url` always an http(s) URL — built from `meeting.code` against
 * this app's origin when the CRM only knows the room code.
 */
export function normalizeEvent(raw, ownOrigin = '') {
  if (!raw || typeof raw !== 'object') return null;
  const start = toMs(raw.start);
  const end = toMs(raw.end);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const meeting = raw.meeting && typeof raw.meeting === 'object' ? raw.meeting : {};
  let url = typeof meeting.url === 'string' ? meeting.url : '';
  if (!url && typeof meeting.code === 'string' && meeting.code && ownOrigin) {
    url = `${ownOrigin}/${encodeURIComponent(meeting.code)}`;
  }
  return { ...raw, start, end, meeting: { ...meeting, url } };
}

/**
 * @param {object} [options]
 * @param {string} [options.baseUrl]        where the backend serves the member's meetings (default: /v1/me)
 * @param {() => Promise<unknown>} [options.refreshToken]
 * @param {typeof fetch} [options.fetchImpl]
 * @param {() => number} [options.now]
 * @param {() => boolean} [options.signedIn]
 * @param {() => Record<string,string>} [options.headers] extra request headers (none by default: the browser holds no CRM token)
 * @param {string} [options.ownOrigin]
 */
export function createCrm({
  baseUrl = '/v1/me',
  // Called when the server has no CRM token for the member yet (after a restart): renews it (a silent
  // check), after which the read is tried once more.
  refreshToken = null,
  fetchImpl = (...args) => fetch(...args),
  now = () => Date.now(),
  signedIn = isSignedIn,
  headers = () => ({}),
  ownOrigin = typeof location !== 'undefined' ? location.origin : '',
} = {}) {
  let failures = 0;
  let blockedUntil = 0;
  const listeners = new Set();

  const supported = () => !!baseUrl && signedIn();
  const available = () => supported() && now() >= blockedUntil;

  function succeeded() {
    failures = 0;
    blockedUntil = 0;
  }
  function failed() {
    failures += 1;
    blockedUntil = now() + Math.min(MAX_RETRY_MS, RETRY_AFTER_MS * 2 ** (failures - 1));
  }

  return {
    supported,
    available,
    retryIn: () => Math.max(0, blockedUntil - now()),
    clearBackoff: succeeded,
    onChange(fn) {
      if (typeof fn !== 'function') return () => {};
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    /** Tell subscribers the meetings changed (the embedding page can call this via postMessage). */
    notifyChanged() {
      for (const fn of [...listeners]) fn();
    },

    /**
     * Events with a meeting link in [from, to) — epoch milliseconds — earliest
     * first. Resolves { events, total }; rejects with CrmApiError when the CRM
     * refused, or with the network error when it could not be reached.
     */
    async listMeetings({ from, to }) {
      if (!supported()) throw new Error('No CRM is connected.');
      const url = new URL(`${baseUrl}/meetings`, ownOrigin || 'http://localhost');
      url.searchParams.set('from', new Date(from).toISOString());
      url.searchParams.set('to', new Date(to).toISOString());
      url.searchParams.set('limit', String(LIST_LIMIT));
      const ask = () =>
        fetchImpl(url.pathname + url.search, {
          headers: { accept: 'application/json', ...headers() },
          credentials: 'same-origin',
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
      let res;
      try {
        res = await ask();
        // The server holds no CRM token for the member yet (it restarted): renew it, then ask once more.
        if (res.status === 401 && refreshToken) {
          const why = await res.clone().json().catch(() => null);
          if (why?.reason === 'token') {
            await refreshToken();
            res = await ask();
          }
        }
      } catch (error) {
        failed();
        throw error;
      }
      if (!res.ok) {
        // A refusal proves the server is there; only server errors count as unreachable.
        if (res.status >= 500) failed();
        else succeeded();
        throw new CrmApiError(res.status);
      }
      let body;
      try {
        body = await res.json();
      } catch {
        failed();
        throw new CrmApiError(res.status, 'The CRM sent an unreadable answer');
      }
      succeeded();
      const events = (Array.isArray(body?.events) ? body.events : []).map((e) => normalizeEvent(e, ownOrigin)).filter(Boolean);
      return { events, total: Number.isFinite(body?.total) ? body.total : events.length };
    },

    resolveLink: (event) => normalizeEvent(event, ownOrigin)?.meeting.url ?? null,

    dispose() {
      listeners.clear();
      succeeded();
    },
  };
}

/** A CRM that answers from a fixed list — for tests and for running the UI with no CRM. */
export function createMockCrm(events = []) {
  return {
    supported: () => true,
    available: () => true,
    retryIn: () => 0,
    clearBackoff() {},
    onChange: () => () => {},
    async listMeetings({ from, to }) {
      const inRange = events.filter((e) => toMs(e.end) > from && toMs(e.start) < to);
      return { events: inRange.map((e) => normalizeEvent(e)).filter(Boolean), total: inRange.length };
    },
    resolveLink: (event) => event?.meeting?.url ?? null,
    dispose() {},
  };
}
